import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Security,
  Warning,
  Block,
  Build,
  Refresh,
  ExpandMore,
  Shield,
  Lock,
  Person,
  History,
  Settings,
  CheckCircle,
  Error,
  Info,
  PowerSettingsNew,
  Timer,
  ToggleOff,
  ToggleOn
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import emergencyAPI from '../../services/emergencyAPI';
import type { EmergencyStatus } from '../../types/emergency';

const EmergencyControlPanel: React.FC = () => {
  const [status, setStatus] = useState<EmergencyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [duration, setDuration] = useState<number>(60);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await emergencyAPI.getStatus();
      setStatus(response || null);
    } catch (error: any) {
      console.error('Failed to fetch emergency status:', error);
      toast.error('Failed to load emergency status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action: string) => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for this action');
      return;
    }

    setActionLoading(action);
    try {
      const request = {
        reason: reason.trim(),
        duration_minutes: duration,
        admin_note: adminNote.trim() || undefined
      };

      let response;
      switch (action) {
        case 'block-all-api':
          response = await emergencyAPI.blockAllAPI(request);
          break;
        case 'enable-maintenance':
          response = await emergencyAPI.enableMaintenance(request);
          break;
        case 'block-registration':
          response = await emergencyAPI.blockRegistration(request);
          break;
        case 'reset-all':
          response = await emergencyAPI.resetAll(request);
          break;
        default:
          console.error(`Unknown action: ${action}`);
          return;
      }

      toast.success(response.message || `Emergency action "${action}" executed successfully`);
      setDialogOpen(null);
      setReason('');
      setAdminNote('');
      fetchStatus();
    } catch (error: any) {
      console.error('Emergency action failed:', error);
      toast.error(`Failed to execute action: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getEmergencyLevelColor = (level: string, isActive: boolean) => {
    if (!isActive) return 'success';
    
    switch (level) {
      case 'normal': return isActive ? 'warning' : 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      case 'lockdown': return 'error';
      default: return 'default';
    }
  };

  const getEmergencyLevelIcon = (level: string, isActive: boolean) => {
    if (!isActive) return <CheckCircle />;
    
    switch (level) {
      case 'normal': return <Warning />;
      case 'warning': return <Warning />;
      case 'critical': return <Error />;
      case 'lockdown': return <Block />;
      default: return <Info />;
    }
  };

  const getEmergencyDisplayLevel = (level: string, isActive: boolean) => {
    if (!isActive) return 'NORMAL';
    return isActive ? 'EMERGENCY ACTIVE' : level.toUpperCase();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Check if a specific feature is currently blocked
  const isFeatureBlocked = (feature: string) => {
    return status?.active_blocks?.[feature] || false;
  };

  // Get button state and action for toggle functionality
  const getButtonConfig = (feature: string) => {
    const blocked = isFeatureBlocked(feature);
    
    const configs = {
      'all_api': {
        blocked: blocked,
        label: blocked ? 'Unblock All API' : 'Block All API',
        action: blocked ? 'reset-all' : 'block-all-api',
        color: blocked ? 'success' : 'error',
        icon: blocked ? <ToggleOff /> : <Block />
      },
      'registration': {
        blocked: blocked,
        label: blocked ? 'Enable Registration' : 'Block Registration',
        action: blocked ? 'reset-all' : 'block-registration',
        color: blocked ? 'success' : 'info',
        icon: blocked ? <ToggleOn /> : <Person />
      },
      'maintenance': {
        blocked: status?.maintenance_mode || false,
        label: status?.maintenance_mode ? 'Disable Maintenance' : 'Enable Maintenance',
        action: status?.maintenance_mode ? 'reset-all' : 'enable-maintenance',
        color: status?.maintenance_mode ? 'success' : 'warning',
        icon: status?.maintenance_mode ? <ToggleOff /> : <Build />
      }
    };

    return configs[feature as keyof typeof configs];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!status) {
    return (
      <Alert severity="error">
        Failed to load emergency status. Please refresh the page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Security sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Emergency Control Panel</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchStatus}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Emergency Status Alert */}
      {status.is_emergency_active && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          icon={<Warning />}
        >
          <Typography variant="subtitle1">🚨 Emergency Mode Active</Typography>
          <Typography variant="body2">
            Emergency security measures are currently active. Some system features may be restricted.
          </Typography>
          {status.emergency_message && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
              Message: {status.emergency_message}
            </Typography>
          )}
        </Alert>
      )}

      {/* Emergency Status Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getEmergencyLevelIcon(status.emergency_level, status.is_emergency_active)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  System Status
                </Typography>
              </Box>
              <Chip
                label={`Status: ${getEmergencyDisplayLevel(status.emergency_level, status.is_emergency_active)}`}
                color={getEmergencyLevelColor(status.emergency_level, status.is_emergency_active) as any}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                Emergency Active: {status.is_emergency_active ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maintenance Mode: {status.maintenance_mode ? 'Enabled' : 'Disabled'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Shield sx={{ mr: 1 }} />
                <Typography variant="h6">Active Restrictions</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Object.entries(status.active_blocks).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={key.replace('_', ' ')}
                    color={value ? 'error' : 'default'}
                    variant={value ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Blocked Users: {status.blocked_users_count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Blocked IPs: {status.blocked_ips_count}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Smart Toggle Controls */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          🎛️ Emergency Controls
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Toggle emergency features on/off. All actions require a reason and are logged.
        </Typography>
        
        <Grid container spacing={2}>
          {/* Block All API Toggle */}
          <Grid item xs={12} sm={6} md={4}>
            {(() => {
              const config = getButtonConfig('all_api');
              return (
                <Button
                  variant="contained"
                  color={config.color as any}
                  fullWidth
                  startIcon={config.icon}
                  onClick={() => setDialogOpen(config.action)}
                  disabled={!!actionLoading}
                  sx={{ py: 2 }}
                >
                  {config.label}
                </Button>
              );
            })()}
          </Grid>

          {/* Maintenance Mode Toggle */}
          <Grid item xs={12} sm={6} md={4}>
            {(() => {
              const config = getButtonConfig('maintenance');
              return (
                <Button
                  variant="contained"
                  color={config.color as any}
                  fullWidth
                  startIcon={config.icon}
                  onClick={() => setDialogOpen(config.action)}
                  disabled={!!actionLoading}
                  sx={{ py: 2 }}
                >
                  {config.label}
                </Button>
              );
            })()}
          </Grid>

          {/* Registration Toggle */}
          <Grid item xs={12} sm={6} md={4}>
            {(() => {
              const config = getButtonConfig('registration');
              return (
                <Button
                  variant="contained"
                  color={config.color as any}
                  fullWidth
                  startIcon={config.icon}
                  onClick={() => setDialogOpen(config.action)}
                  disabled={!!actionLoading}
                  sx={{ py: 2 }}
                >
                  {config.label}
                </Button>
              );
            })()}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Reset All Emergency Controls */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="success"
            size="large"
            startIcon={<PowerSettingsNew />}
            onClick={() => setDialogOpen('reset-all')}
            disabled={!!actionLoading || !status.is_emergency_active}
            sx={{ px: 4, py: 1.5 }}
          >
            Reset All Emergency Controls
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            This will disable all emergency restrictions and return system to normal operation
          </Typography>
        </Box>
      </Paper>

      {/* Quick Action Dialog */}
      <Dialog open={!!dialogOpen} onClose={() => setDialogOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {(() => {
            const actionMap = {
              'block-all-api': 'Block All API Access',
              'enable-maintenance': 'Enable Maintenance Mode',
              'block-registration': 'Block User Registration',
              'reset-all': 'Reset All Emergency Controls'
            };
            return actionMap[dialogOpen as keyof typeof actionMap] || 'Confirm Action';
          })()}
        </DialogTitle>
        <DialogContent>
          <Alert severity={dialogOpen === 'reset-all' ? 'info' : 'warning'} sx={{ mb: 3 }}>
            {dialogOpen === 'reset-all' 
              ? 'This will disable all emergency controls and return the system to normal operation.'
              : 'This action will be executed immediately and logged. Please provide a clear reason.'
            }
          </Alert>
          
          <TextField
            fullWidth
            label="Reason (Required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            placeholder={dialogOpen === 'reset-all' 
              ? 'e.g., Emergency resolved, returning to normal operations'
              : 'e.g., Security incident detected, blocking API access'
            }
            helperText="Minimum 3 characters required"
          />
          
          <TextField
            fullWidth
            label="Admin Note (Optional)"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="Additional notes for audit trail"
          />
          
          {dialogOpen !== 'reset-all' && (
            <TextField
              fullWidth
              type="number"
              label="Duration (minutes)"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
              inputProps={{ min: 1, max: 1440 }}
              helperText="Set duration for automatic reversal (1-1440 minutes)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(null)} disabled={!!actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => handleQuickAction(dialogOpen!)}
            variant="contained"
            color={dialogOpen === 'reset-all' ? 'success' : 'error'}
            disabled={!reason.trim() || !!actionLoading}
            startIcon={actionLoading === dialogOpen ? <CircularProgress size={16} /> : undefined}
          >
            {actionLoading === dialogOpen ? 'Executing...' : 'Confirm & Execute'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmergencyControlPanel; 